import qs from 'qs'
import axios from 'axios'

const TabeeAuth = (function(){
    const
        { CancelToken } = axios,
        cancels = {},

        config = {
            api: '',
            sso: '',
            token: null,
            options: ( noAuth ) =>
            ({
                validateStatus: null,
                headers: !noAuth && !!config.token
                    ? { Authorization: `Token ${config.token}` }
                    : {}
            })
        },

        request = {
            post: ( endpoint, body, cfg ) => axios.post(
                config.api + '/api/' + endpoint,
                body,
                {
                    ...config.options(),
                    ...cfg
                }
            ),
            get: ( endpoint, params, uniq, cfg, noAuth ) =>
                {
                    const 
                        add = uniq || ''

                    cancels[endpoint + add] && cancels[endpoint + add]()

                    return axios.get(
                        config.api + '/api/' + endpoint, 
                        {
                            params,
                            ...config.options( noAuth ),
                            ...cfg,
                            ...tools.cancel( endpoint + add )
                        }
                    )
                }
        },

        handlers = {
            auth: body =>
                request.post(
                    'auth/login',
                    qs.stringify( body ),
                    {
                        headers: {
                            'Content-Type': 'application/x-www-form-urlencoded'
                        }
                    }
                ),

            auth2fa: body =>
                request.get(
                    `auth/2fa/request`,
                    {
                        email: body.email.indexOf( '%' > -1 ) ? body.email.trim() : encodeURIComponent(body.email.trim()),
                        source: body.source ? tools.generateAuthSource( body.source ) : null
                    },
                    null,
                    {
                        headers: {}
                    }
                ),

            code2fa: body =>
                request.get(
                    `auth/2fa/validate`,
                    {
                        email: body.email.indexOf( '%' > -1 ) ? body.email.trim() : encodeURIComponent(body.email.trim()),
                        code: body.code.trim()
                    },
                    null,
                    {
                        headers: {}
                    }
                ),

            resetPass: body => request.get( 'auth/reset', body, '', { headers: { Authorization: null } } ),

            register: body =>
                request.post(
                    'auth/register',
                    {
                        ...body,
                        project: config.project
                    }, 
                    { headers: { Authorization: null } }
                ),

            logout: () => request.get( 'auth/register' ),

            confirm: {
                user: body =>
                    request.get(
                        `auth/confirm/${body.code}?format=json`
                    ),

                link: body =>
                    request.get(
                        `links/validate_recipient/${body.code}?format=json`
                    ),
            },

            validate: {
                user: body =>
                    request.get(
                        `auth/confirm/${body.token}?format=json`,
                        {},
                        null,
                        {},
                        true
                    ),
            }
        },

        tools = {
            prepare: event => ({
                status: event.status === 200,
                message: event.status !== 200
                    ? event.data[ Object.keys( event.data )[0] ]
                    : '',
                data: event.data,
                token: event.data.token,
                user: event.data.user
            }),

            generateAuthSource: raw => {
                const
                    res = {
                        redirect_uri: raw.redirect_uri,
                        return_url: raw.return_url,
                        platform: 'web'
                    }

                return encodeURIComponent(btoa(JSON.stringify( res )))
            },
            
            cancel: name =>
                ({
                    cancelToken: new CancelToken( function executor ( token ) { cancels[name] = token } )
                }),

            signIn: handler => params => 
                new Promise(( resolve, reject ) => {
                    handler( params )
                        .then( event => {

                            if ( event.status >= 300 ) {
                                reject({
                                    ...event,
                                    status: event.status === 200,
                                    error: true,
                                    data: event.data || null,
                                    code: event.message,
                                    message: event.toJSON ? event.toJSON() : ( event.data.message || event.data.status || event.data.detail || 'Error' )
                                })
                            } else {
                                resolve( tools.prepare( event )) 
                            }                        
                        })
                        .catch( 
                            event => {
                                if ( axios.isCancel( event ) ) return
        
                                return (
                                    typeof event === 'object' && event.status
                                        ? resolve( tools.prepare( event ) )
                                        : reject({
                                            ...event,
                                            error: true,
                                            data: event.data || null,
                                            code: event.message,
                                            message: event.toJSON()
                                        })
                                )
                            }
                        )
                }),
        }

    return {
        setApi: url => config.api = url,
        setSSO: url => config.sso = url,
        setProject: code => config.project = code,
        setToken: token => config.token = token,
        setConfig: cfg => Object.keys( cfg ).forEach( key => config[key] = cfg[key] ),

        isReady: () => !!config.api,

        logOut: () => handlers.logout(),

        resetPass: params => 
            new Promise(( resolve ) => {
                handlers.resetPass( params )
                    .then( event => {
                        let
                            msg = '',
                            sent = false
                
                        switch ( event.data.detail ) {
                            case 'confirm reset email sent':
                                msg = 'Confirm reset email sent'
                                sent = true
                                break

                            case 'Not found':
                                msg = 'Email not found'
                                break

                            default:
                                //Do nothing
                        }
                
                        resolve({ message: msg, status: sent })
                    })
            }),

        register: params => 
            new Promise(( resolve, reject ) => {
                handlers.register( params )
                    .then( event => {
                        let
                            status = event.status === 200,
                            msg = ''

                        if ( !status ) {
                            ;( !!event.data.email ) && ( msg = event.data.email[0].replace( 'This field', 'Email' ) )
                            ;( !!event.data.username ) && ( msg = event.data.username )
                        }

                        resolve({
                            status,
                            token:  event.data.token,
                            message: msg,
                            data: event.data,
                            user: event.data.user
                        })
                    })
            }),

        signIn: tools.signIn( handlers.auth ),
        signIn2fa: tools.signIn( handlers.auth2fa ),
        signInCode: tools.signIn( handlers.code2fa ),

        SSOSignIn: ( redirect_uri, return_url ) => {
            window.location.href = `${config.sso}?redirect_uri=${encodeURIComponent(redirect_uri)}${ return_url ? `&return_url=${encodeURIComponent(return_url)}` : '' }`
        },

        SSOSignUp: ( redirect_uri, return_url ) => {
            window.location.href = `${config.sso}/sign-up?redirect_uri=${encodeURIComponent(redirect_uri)}${ return_url ? `&return_url=${encodeURIComponent(return_url)}` : '' }`
        },

        confirmUser: code => 
            new Promise(( resolve, reject ) => {
                handlers.confirm.user({ code })
                    .then( event => {
                        resolve( event.data )
                    })
            }),

        confirmLink: code => 
            new Promise(( resolve, reject ) => {
                handlers.confirm.link({ code })
                    .then( event => {
                        resolve( event.data )
                    })
            }),

        validateUser: token =>
            new Promise(( resolve, reject ) => {
                handlers.validate.user({ token })
                    .then( event => {
                        resolve( event.data )
                    })
                    .catch(err => {
                        resolve( err )
                    })
            })
    }
})()

export default TabeeAuth